import { io, Socket } from 'socket.io-client';
import { getClientId } from '@/lib/clientId';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const REALTIME_NAMESPACE = '/realtime';

/** Mirror of `EntityChangedEvent` in the API's `realtime/realtime.types.ts`. */
export interface EntityChangedEvent {
  /** Audit entity type — singular: `patient`, `reservation`, `invoice`. */
  entity: string;
  id: string;
  action: string;
  clinicId: string;
  branchId: string | null;
  subjectType: string | null;
  subjectId: string | null;
  actorId: string | null;
  /** The tab that made the change; equals `getClientId()` when it was this one. */
  clientId: string | null;
  at: string;
}

type EventListener = (event: EntityChangedEvent) => void;
/** Argument-free to match `useSyncExternalStore`; read `isConnected()` for the value. */
type StatusListener = () => void;

/**
 * The clinic-wide change feed — "record X changed, go look".
 *
 * One socket for the whole app, opened once the user has a clinic and closed
 * on logout. It joins nothing by name: the server puts it in the clinic room
 * that the access-token cookie says it belongs to, so `withCredentials` is
 * what authenticates it.
 *
 * Reconnection is unlimited, like the queue socket and unlike the
 * notification one: a front-desk tab that quietly stops receiving after a
 * WiFi blip is a screen showing yesterday's schedule with nobody the wiser.
 * `onReconnect` exists so the app can refetch everything it missed while
 * the socket was down — the feed carries no history.
 */
class RealtimeService {
  private socket: Socket | null = null;
  private eventListeners = new Set<EventListener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectListeners = new Set<() => void>();
  private wasConnected = false;
  /** Set by `connect()`, cleared by `disconnect()`: whether the app wants a socket right now. */
  private wanted = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    this.wanted = true;
    if (this.socket) return;

    this.socket = io(`${SOCKET_URL}${REALTIME_NAMESPACE}`, {
      transports: ['websocket'],
      withCredentials: true,
      // Its own connection. socket.io-client otherwise multiplexes every
      // namespace of one host over the first Manager created for it — the
      // notification bell's — and that Manager's reconnect policy (and its
      // give-up after five attempts) would then be this feed's too.
      forceNew: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15_000,
      reconnectionAttempts: Infinity,
      timeout: 20_000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      // The first connect is not a reconnect: nothing was missed yet.
      if (this.wasConnected) {
        for (const listener of this.reconnectListeners) listener();
      }
      this.wasConnected = true;
      this.notifyStatus();
    });
    this.socket.on('disconnect', () => this.notifyStatus());
    this.socket.on('connect_error', () => this.notifyStatus());

    // The server rejected the cookie: the access token expired while the
    // tab was asleep, or the user has not picked a clinic yet. socket.io
    // would retry every second with the same dead cookie, so stop it and
    // try again later — by then the HTTP client's refresh interceptor will
    // have minted a new token on the user's next request.
    this.socket.on('realtime:unauthorized', () => {
      this.teardown();
      if (!this.wanted) return;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        if (this.wanted) this.connect();
      }, 30_000);
    });

    this.socket.on('entity.changed', (event: EntityChangedEvent) => {
      for (const listener of this.eventListeners) {
        try {
          listener(event);
        } catch (error) {
          console.error('Realtime listener failed:', error);
        }
      }
    });
  }

  disconnect(): void {
    this.wanted = false;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.teardown();
  }

  /** Drop and re-open the socket — after a clinic switch, so the new cookie is presented. */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  private teardown(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.wasConnected = false;
    this.notifyStatus();
  }

  onEvent(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onReconnect(listener: () => void): () => void {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  /**
   * Ask every screen to reload what it shows, as after a reconnect. Used by
   * pull-to-refresh: the reconnect listeners are the one hook that reaches
   * both React Query and the screens that fetch by hand.
   */
  refreshAll(): void {
    for (const listener of this.reconnectListeners) listener();
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /** True when this tab made the change the event describes. */
  isOwn(event: EntityChangedEvent): boolean {
    return event.clientId !== null && event.clientId === getClientId();
  }

  private notifyStatus() {
    for (const listener of this.statusListeners) listener();
  }
}

export const realtimeService = new RealtimeService();
